import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import AdminShell, { Tabs, Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

export default function CmsAdmin() {
  const [tab, setTab] = useState("announcements");
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const pager = useAdminPager();

  async function load() {
    const url = tab === "announcements" ? "/api/admin/cms/announcements" : "/api/admin/cms/banners";
    const { data } = await api.get(url, { params: { skip: pager.skip, limit: pager.limit } });
    const { items, total } = unwrapAdminList(data, tab === "announcements" ? "announcements" : "banners");
    setList(items);
    pager.setTotal(total);
  }
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, pager.page]);

  async function del(id) {
    if (!window.confirm("Delete?")) return;
    const url = tab === "announcements" ? `/api/admin/cms/announcements/${id}` : `/api/admin/cms/banners/${id}`;
    await api.delete(url);
    load();
  }

  return (
    <AdminShell title="CMS & Announcements">
      <Tabs tabs={[
        { id: "announcements", label: "Announcements" },
        { id: "banners", label: "Banners" },
        { id: "notifications", label: "Notifications" },
      ]} value={tab} onChange={setTab} />

      {tab === "announcements" && (
        <Panel>
          <div className="p-4 border-b border-[#1E2845] flex justify-between">
            <div className="text-[14px] font-bold">Site Announcements</div>
            <Btn onClick={() => setEditing({ type: "info", is_active: true, show_on_landing: true, show_in_app: true, title: "", body: "" })} data-testid="new-announcement"><Plus size={14} /> New</Btn>
          </div>
          <table className="w-full text-[13px]" data-testid="announcements-table">
            <thead className="bg-[#0A0F1F] text-[#8892A8] text-left text-[11px] uppercase tracking-wider">
              <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Active</th><th className="px-5 py-3">Visibility</th><th className="px-5 py-3">Created</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-[#1E2845]">
              {list.length === 0 ? <tr><td colSpan={6} className="p-10 text-center text-[#8892A8]">No announcements. Click "New" to publish.</td></tr> :
                list.map((a) => (
                  <tr key={a.id} className="hover:bg-[#13192C]" data-testid={`announcement-${a.id}`}>
                    <td className="px-5 py-3"><div className="font-bold">{a.title}</div><div className="text-[11px] text-[#8892A8] truncate max-w-[400px]">{a.body}</div></td>
                    <td className="px-5 py-3"><StatusPill value={a.type} /></td>
                    <td className="px-5 py-3"><StatusPill value={a.is_active ? "active" : "disabled"} /></td>
                    <td className="px-5 py-3 text-[11px] text-[#8892A8]">
                      {a.show_on_landing && "🌐 Landing  "}
                      {a.show_in_app && "📱 In-App"}
                    </td>
                    <td className="px-5 py-3 text-[#8892A8] text-[11px]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setEditing(a)} className="text-[#F0B83A] text-[12px] hover:underline mr-3">Edit</button>
                      <button onClick={() => del(a.id)} className="text-[#EF4444] text-[12px] hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <AdminPager
            page={pager.page}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
            testId="cms-pager"
          />
        </Panel>
      )}

      {tab === "banners" && (
        <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Banners — wires in next iteration with image upload.</Panel>
      )}
      {tab === "notifications" && (
        <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Push notifications — wires after FCM/APNs integration.</Panel>
      )}

      {editing && <AnnouncementModal data={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </AdminShell>
  );
}

function AnnouncementModal({ data, onClose, onSaved }) {
  const isEdit = !!data.id;
  const [f, setF] = useState({ ...data });
  async function save(e) {
    e.preventDefault();
    if (isEdit) await api.patch(`/api/admin/cms/announcements/${data.id}`, f);
    else await api.post("/api/admin/cms/announcements", f);
    onSaved();
  }
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-[#0F1626] border border-[#1E2845] rounded-[14px] w-full max-w-[520px] p-5 space-y-3" data-testid="announcement-modal">
        <h3 className="text-[16px] font-bold">{isEdit ? "Edit announcement" : "New announcement"}</h3>
        <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ann-title" className="w-full h-[40px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" />
        <textarea required rows={3} placeholder="Body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="ann-body" className="w-full p-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]" />
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full h-[40px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[13px]">
          {["info", "success", "warning", "critical"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <div className="flex gap-4 text-[13px]">
          <Toggle label="Active" v={f.is_active} on={(v) => setF({ ...f, is_active: v })} />
          <Toggle label="Landing" v={f.show_on_landing} on={(v) => setF({ ...f, show_on_landing: v })} />
          <Toggle label="In-app" v={f.show_in_app} on={(v) => setF({ ...f, show_in_app: v })} />
        </div>
        <Btn type="submit" className="w-full" data-testid="ann-save">Save</Btn>
      </form>
    </div>
  );
}

function Toggle({ label, v, on }) {
  return <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} /> {label}</label>;
}
